import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Component, effect, Inject, signal } from '@angular/core';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { AlertComponent } from '@vendasta/galaxy/alert/src/alert.component';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { encodeFilters } from '@vendasta/galaxy/filter/chips/src/utils';
import { GalaxyFilterOperator } from '@vendasta/galaxy/filter/chips';
import { SalesCoachConfig, SalesCoachUserConfig } from '@vendasta/protos/meeting_analysis/v1';
import * as SalesCoachConfigService from '@vendasta/protos/meeting_analysis/v1/sales-coach-config-service';
import { catchError, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import { StandardIds } from '@galaxy/crm/services/field-service';
import {
  CONTEXT_DATA_TOKEN,
  ContextData,
  CRM_ACTIVITY_FEED_PAGE_TOKEN,
  MEETING_SETTINGS_LIST_VIEW_PAGE_TOKEN,
  Context,
} from '../data-providers/providers';
import { MeetingSchedulerStoreService } from '../data-providers/meeting-scheduler-store.service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AvailableServices, MeetingViewModelService } from '@vendasta/integrations';
import { MatIconModule } from '@angular/material/icon';
import { MeetingListService } from '@galaxy/meeting-analysis';
import { CalendarSource, MeetingSource } from '@vendasta/meetings';

@Component({
  selector: 'meeting-scheduler-my-meetings',
  templateUrl: './my-meetings.component.html',
  styleUrl: './my-meetings.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    TranslateModule,
    GalaxyPageModule,
    RouterModule,
    AlertComponent,
    GalaxyAlertModule,
    MatIconModule,
  ],
})
export class MyMeetingsComponent {
  private readonly BOOKINGS_ALERT_KEY = 'my-meetings-bookings-alert-dismissed';
  private readonly RECORDINGS_ALERT_KEY = 'my-meetings-recordings-alert-dismissed';

  namespace$: Observable<string>;
  contextType$: Observable<Context>;
  userId$: Observable<string>;
  salesCoachUserConfig$: Observable<SalesCoachUserConfig>;
  salesCoachConfig$: Observable<SalesCoachConfig>;

  isCalendarDisconnected$: Observable<boolean>;
  isMeetingAppDisconnected$: Observable<boolean>;
  hasDisconnectedIntegrations$: Observable<boolean>;

  // Signals for recordings and upgrade alerts
  readonly showRecordingDisabledAlert = signal<boolean | undefined>(undefined);
  readonly showTierUpgradeRequiredAlert = signal<boolean | undefined>(undefined);
  readonly showTierUpgradeIcon = signal<boolean | undefined>(undefined);
  readonly showRecordingsDismissableAlert = signal<boolean | undefined>(undefined);
  readonly hasRecordings = signal<boolean | undefined>(undefined);

  // This magic string is so that we don't have to import all of meeting scheduler into business app proper as meeting scheduler doesn't currently
  // have a `static` module to avoid this.
  constructor(
    @Inject('show_my_meetings_recording_tab$') readonly showMyMeetingsRecordingTab$: Observable<boolean>,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    @Inject(CONTEXT_DATA_TOKEN) private readonly contextData$: Observable<ContextData>,
    @Inject(MEETING_SETTINGS_LIST_VIEW_PAGE_TOKEN) private readonly settingsLink$: Observable<string>,
    @Inject(CRM_ACTIVITY_FEED_PAGE_TOKEN) public readonly activityFeedPageLink$: Observable<string>,
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private readonly meetingViewModelService: MeetingViewModelService,
    private readonly meetingListService: MeetingListService,
  ) {
    this.initializeObservables();

    effect(() => {
      this.initializeRecordingsAlerts();
    });
  }

  private initializeObservables(): void {
    this.namespace$ = this.contextData$.pipe(map((data) => data.namespace));
    this.contextType$ = this.contextData$.pipe(map((data) => data.type));

    this.userId$ = of(this.meetingSchedulerStoreService.getUserId());

    this.salesCoachConfig$ = this.namespace$.pipe(
      switchMap((namespace) => {
        return SalesCoachConfigService.get$({ namespace: namespace });
      }),
      map((resp) => {
        return resp?.salesCoachConfig;
      }),
      catchError((err) => {
        if (err.status !== 404) {
          this.snackbarService.openErrorSnack(
            this.translateService.instant('MMEETING_SCHEDULER.SALES_COACH_CONFIG.LOAD_ERROR'),
          );
        }
        return of({ joinMeetings: false } as SalesCoachConfig);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.salesCoachUserConfig$ = combineLatest([this.namespace$, this.userId$]).pipe(
      switchMap(([namespace, userId]) => {
        return SalesCoachConfigService.getUserConfig$({
          namespace: namespace,
          userId: userId,
        });
      }),
      map((resp) => {
        const config = resp?.salesCoachUserConfig;
        return config;
      }),
      catchError((err) => {
        if (err.status !== 404) {
          this.snackbarService.openErrorSnack(
            this.translateService.instant('MEETING_SCHEDULER.SALES_COACH_CONFIG.LOAD_ERROR'),
          );
        }
        return of({ joinMeetings: false } as SalesCoachUserConfig);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    const preferences$ = this.meetingSchedulerStoreService
      .loadUserPreferences()
      .pipe(shareReplay({ refCount: true, bufferSize: 1 }));

    const isConnected$ = (source: AvailableServices) => this.meetingViewModelService.isServiceConnected$(source);

    this.isCalendarDisconnected$ = preferences$.pipe(
      switchMap((p) => {
        // CALENDAR_SOURCE_GOOGLE = 0 is falsy, so PreferencesFromApi maps it to CALENDAR_SOURCE_INVALID (-1) via `||`.
        // Treat INVALID as Google to work around this library bug.
        const cal = p?.calendarIntegration;
        if (cal === CalendarSource.CALENDAR_SOURCE_GOOGLE || cal === CalendarSource.CALENDAR_SOURCE_INVALID) {
          return isConnected$(AvailableServices.GOOGLE_MEET).pipe(map((c) => !c));
        } else if (cal === CalendarSource.CALENDAR_SOURCE_MICROSOFT) {
          return isConnected$(AvailableServices.MICROSOFT).pipe(map((c) => !c));
        }
        return of(false);
      }),
      startWith(false),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.isMeetingAppDisconnected$ = preferences$.pipe(
      switchMap((p) => {
        if (p?.meetingIntegration === MeetingSource.MEETING_SOURCE_GOOGLE_MEET) {
          return isConnected$(AvailableServices.GOOGLE_MEET).pipe(map((c) => !c));
        } else if (p?.meetingIntegration === MeetingSource.MEETING_SOURCE_MICROSOFT) {
          return isConnected$(AvailableServices.MICROSOFT).pipe(map((c) => !c));
        } else if (p?.meetingIntegration === MeetingSource.MEETING_SOURCE_ZOOM) {
          return isConnected$(AvailableServices.ZOOM).pipe(map((c) => !c));
        }
        return of(false);
      }),
      startWith(false),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.hasDisconnectedIntegrations$ = combineLatest([
      this.isCalendarDisconnected$,
      this.isMeetingAppDisconnected$,
    ]).pipe(
      map(([cal, meeting]) => cal || meeting),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private async initializeRecordingsAlerts(): Promise<void> {
    try {
      const contextType = await firstValueFrom(this.contextType$);
      const userSalesCoachConfig = await firstValueFrom(this.salesCoachUserConfig$);
      const salesCoachConfig = await firstValueFrom(this.salesCoachConfig$);
      const isAlertDismissed = !!localStorage.getItem(this.RECORDINGS_ALERT_KEY);

      this.showRecordingsDismissableAlert.set(
        !isAlertDismissed && !!userSalesCoachConfig?.joinMeetings && !!salesCoachConfig?.joinMeetings,
      );
      this.showRecordingDisabledAlert.set(!userSalesCoachConfig?.joinMeetings && !!salesCoachConfig?.joinMeetings);

      if (!salesCoachConfig?.joinMeetings && contextType === Context.PARTNER) {
        this.showTierUpgradeIcon.set(true);
        // only check for recordings if the user doesn't have access to "joinMeetings" feature
        await this.checkForRecordings();
        this.showTierUpgradeRequiredAlert.set(this.hasRecordings());
      } else {
        this.showTierUpgradeRequiredAlert.set(false);
        this.showTierUpgradeIcon.set(false);
      }
    } catch (error) {
      console.error('Error loading sales coach config:', error);
      this.showRecordingDisabledAlert.set(false);
      this.showTierUpgradeRequiredAlert.set(false);
    }
  }

  get showBookingsAlert(): boolean {
    return !localStorage.getItem(this.BOOKINGS_ALERT_KEY);
  }

  onBookingsAlertClose(): void {
    localStorage.setItem(this.BOOKINGS_ALERT_KEY, 'true');
  }

  onRecordingsAlertClose(): void {
    localStorage.setItem(this.RECORDINGS_ALERT_KEY, 'true');
  }

  navigateToCrmRecordedMeetings(): void {
    const filters = [
      {
        fieldId: StandardIds.ActivityMeetingStatus,
        filterId: 'activity-meeting-status-filter',
        operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
        values: [{ string: 'Completed' }],
      },
      {
        fieldId: StandardIds.ActivitySourceName,
        filterId: 'activity-source-name-filter',
        operator: GalaxyFilterOperator.FILTER_OPERATOR_IS_ANY,
        values: [{ string: 'Meeting Analysis' }, { string: 'My Meetings' }, { string: 'Conversation Analysis' }],
      },
    ];
    const filterParam = encodeFilters(filters);
    this.activityFeedPageLink$.pipe(take(1)).subscribe((activityFeedLink) => {
      this.router.navigate([`${activityFeedLink}`], { queryParams: { filter: filterParam } });
    });
  }

  navigateToSettings(): void {
    this.settingsLink$.pipe(take(1)).subscribe((settingsUrl) => {
      this.router.navigateByUrl(settingsUrl);
    });
  }

  reconnectIntegrations(): void {
    // Navigate to meeting-settings page for reconnecting calendar/meeting apps
    // Uses the same approach as navigateToSettings() to get the proper URL from DI token
    this.settingsLink$.pipe(take(1)).subscribe((settingsUrl) => {
      this.router.navigateByUrl(settingsUrl);
    });
  }

  get isBookingsActive(): boolean {
    return this.router.url.includes('/bookings');
  }

  get isRecordingsActive(): boolean {
    return this.router.url.includes('/recordings');
  }

  comparePlans(): void {
    this.router.navigateByUrl('/upgrade');
  }

  async hasAnyAlert(): Promise<boolean> {
    return (
      (this.isBookingsActive && this.showBookingsAlert) ||
      (this.isRecordingsActive && this.showRecordingDisabledAlert())
    );
  }

  private async checkForRecordings(): Promise<void> {
    try {
      const conversations = await firstValueFrom(this.meetingListService.listRecordings('', 1));
      this.hasRecordings.set(!!conversations && conversations.length > 0);
    } catch (error) {
      console.error('Error checking for recordings:', error);
      this.hasRecordings.set(false);
    }
  }
}
